import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { Globe, Pen, Palette, ChevronDown } from 'lucide-react';

const icons = [Globe, Pen, Palette];
const bgImages = [
  '/DISENOWEB.avif',
  '/CREACIONDECONTENIDO.avif',
  '/BRANDING.avif',
];

export default function Services() {
  const { t } = useLanguage();
  const [hovered, setHovered] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 px-6 md:px-12"
      style={{ background: '#050505' }}
    >
      {/* Todo este bloque es inerte en escritorio (media query < 768px). */}
      <style>{`
        @media (max-width: 767px) {
          .services-desc-clamp {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }
          .services-desc-open {
            display: -webkit-box;
            -webkit-line-clamp: 99;
            -webkit-box-orient: vertical;
            max-height: 180px !important;
          }
        }
      `}</style>

      <div className="max-w-7xl mx-auto">
        {/* Letter-by-letter reveal title */}
        <div
          className="overflow-hidden mb-16 md:mb-20"
          style={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)' }}
        >
          <div className="flex justify-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              className="flex overflow-hidden"
              data-testid="services-label"
            >
              {t.services.label.split('').map((char, i) => (
                <motion.span
                  key={i}
                  variants={{
                    hidden: { y: '100%', opacity: 0 },
                    visible: { y: 0, opacity: 1 },
                  }}
                  transition={{
                    duration: 0.6,
                    delay: i * 0.06,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="inline-block text-white italic"
                  style={{
                    fontFamily: "'Instrument Serif', serif",
                    fontSize: 'clamp(5rem, 15vw, 18rem)',
                    lineHeight: 0.95,
                  }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>
              ))}
            </motion.h2>
          </div>
        </div>

        {/* Stacked service cards - vertical flex */}
        <div
          className="flex flex-col gap-4 md:gap-5"
          style={{ height: 'clamp(500px, 70vh, 800px)' }}
          onMouseLeave={() => setHovered(null)}
        >
          {t.services.items.map((service, i) => {
            const Icon = icons[i];
            let flexStyle = {};
            if (typeof window !== 'undefined' && window.innerWidth >= 768) {
              if (hovered === null) {
                flexStyle = { flex: '1 1 33.33%' };
              } else if (hovered === i) {
                flexStyle = { flex: '2.2 1 0%' };
              } else {
                flexStyle = { flex: '0.4 1 0%' };
              }
            }

            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                data-testid={`service-item-${i}`}
                className="relative rounded-2xl px-8 md:px-10 flex items-end overflow-hidden cursor-pointer min-h-[120px]"
                style={{
                  ...flexStyle,
                  transition: 'flex 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)',
                }}
                onMouseEnter={() => setHovered(i)}
              >
                {/* Background image */}
                <div className="absolute inset-0 overflow-hidden rounded-2xl">
                  <img
                    src={bgImages[i]}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700"
                    style={{ transform: hovered === i ? 'scale(1.05)' : 'scale(1)' }}
                  />
                </div>

                {/* Dark overlay */}
                <div
                  className="absolute inset-0 transition-opacity duration-500"
                  style={{
                    background: 'rgba(5, 5, 5, 0.78)',
                    opacity: hovered === i ? 0.55 : 1,
                  }}
                />

                {/* Glass border */}
                <div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{
                    border: hovered === i
                      ? '1px solid rgba(225, 65, 65, 0.4)'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: hovered === i
                      ? 'inset 0 1px 0 rgba(255,255,255,0.15), 0 8px 32px rgba(225,65,65,0.15)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.08)',
                    transition: 'border 0.5s, box-shadow 0.5s',
                  }}
                />

                {/* Content: icon + title + desc in a row */}
                <div className="relative z-10 w-full flex items-center gap-6 md:gap-10 py-6 md:py-8">
                  <div
                    className="w-12 h-12 shrink-0 border rounded-xl flex items-center justify-center transition-all duration-500"
                    style={{
                      borderColor: hovered === i ? 'rgba(225, 65, 65, 0.5)' : 'rgba(255, 255, 255, 0.1)',
                      background: hovered === i ? 'rgba(225, 65, 65, 0.1)' : 'transparent',
                    }}
                  >
                    <Icon
                      size={20}
                      className="transition-colors duration-500"
                      style={{ color: hovered === i ? '#e14141' : 'rgba(255,255,255,0.3)' }}
                    />
                  </div>

                  <div className="flex-1">
                    <h3 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-white tracking-tight">
                      {service.title}
                    </h3>
                    <p
                      className={`font-body text-sm text-zinc-400 leading-relaxed max-w-xl mt-1 transition-all duration-500 overflow-hidden ${hovered === i ? 'services-desc-open' : 'services-desc-clamp'}`}
                      style={{
                        maxHeight: hovered === i || hovered === null ? '60px' : '0px',
                        opacity: hovered === i || hovered === null ? 1 : 0,
                      }}
                    >
                      {service.desc}
                    </p>
                  </div>

                  {/* Chevron indicador — solo visible en móvil */}
                  <ChevronDown
                    size={22}
                    className="md:hidden shrink-0"
                    style={{
                      transform: hovered === i ? 'rotate(180deg)' : 'rotate(0deg)',
                      color: hovered === i ? '#e14141' : 'rgba(255,255,255,0.3)',
                      transition: 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1), color 0.5s ease',
                    }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
